/* =========================================================================
   Penny Premium 3D — tilt.js
   3D card tilt for [data-tilt] frames (product cards, featured-collection
   cards, story-split detail). Pointer-tracked rotateX/rotateY (max ±5°,
   halved under Subtle intensity), damped toward the pointer each rAF
   (lerp 0.12), easing flat on leave. A soft sheen follows the pointer
   via CSS vars. Runs ONLY under html.motion-full.has-fine-pointer
   .has-hover with the theme tilt setting on. will-change is applied on
   enter and removed once the card settles flat; the loop stops when
   nothing is tilting or the tab is hidden.
   ========================================================================= */

const html = document.documentElement;

const enabled = () =>
  html.dataset.tilt !== 'false' &&
  html.classList.contains('motion-full') &&
  html.classList.contains('has-fine-pointer') &&
  html.classList.contains('has-hover');

const MAX_DEG = 5;
const LERP = 0.12;
const active = new Map(); // element → state
let rafId = null;

const scale = () => (html.classList.contains('motion-subtle') ? 0.5 : 1);

function frame() {
  rafId = null;
  let anyLive = false;

  active.forEach((state, el) => {
    state.rx += (state.trx - state.rx) * LERP;
    state.ry += (state.try_ - state.ry) * LERP;

    const settled =
      !state.hovering && Math.abs(state.rx) < 0.02 && Math.abs(state.ry) < 0.02;

    if (settled) {
      el.style.removeProperty('--tilt-rx');
      el.style.removeProperty('--tilt-ry');
      el.style.willChange = '';
      el.classList.remove('is-tilting');
      active.delete(el);
      return;
    }

    el.style.setProperty('--tilt-rx', `${state.rx.toFixed(2)}deg`);
    el.style.setProperty('--tilt-ry', `${state.ry.toFixed(2)}deg`);
    anyLive = true;
  });

  if (anyLive && !document.hidden) rafId = requestAnimationFrame(frame);
}

const wake = () => {
  if (!rafId && !document.hidden) rafId = requestAnimationFrame(frame);
};

document.addEventListener('pointerenter', (event) => {
  if (!enabled()) return;
  const el = event.target.closest?.('[data-tilt]');
  if (!el || active.get(el)?.hovering) return;

  const state = active.get(el) || { rx: 0, ry: 0, trx: 0, try_: 0 };
  state.hovering = true;
  state.rect = el.getBoundingClientRect(); // cached — no reads in the loop
  active.set(el, state);
  el.style.willChange = 'transform';
  el.classList.add('is-tilting');
  wake();
}, true);

document.addEventListener('pointermove', (event) => {
  const el = event.target.closest?.('[data-tilt]');
  const state = el && active.get(el);
  if (!state?.hovering) return;

  const { rect } = state;
  const px = (event.clientX - rect.left) / rect.width; // 0..1
  const py = (event.clientY - rect.top) / rect.height;
  const max = MAX_DEG * scale();

  state.try_ = (px - 0.5) * 2 * max;
  state.trx = (0.5 - py) * 2 * max;
  el.style.setProperty('--sheen-x', `${(px * 100).toFixed(1)}%`);
  el.style.setProperty('--sheen-y', `${(py * 100).toFixed(1)}%`);
  wake();
});

document.addEventListener('pointerleave', (event) => {
  const el = event.target.closest?.('[data-tilt]');
  const state = el && active.get(el);
  if (!state) return;
  state.hovering = false;
  state.trx = 0;
  state.try_ = 0;
  wake();
}, true);

document.addEventListener('visibilitychange', () => {
  if (document.hidden && rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  } else {
    wake();
  }
});
